import { scoutcardExportQueryOptions, scoutcardRecordsQueryOptions, useCreateDailyExportMutation, useCreateDeltaExportMutation, useCreateFullExportMutation } from '@/api/scoutcardOptions';
import { ApiErrorComponent } from '@/components/ApiErrorComponent';
import useGridPageSize from '@/components/hooks/useGridPageSize';
import { usePersistedVisibilityModel } from '@/components/hooks/usePersistedVisibilityModel';
import useResponsive from '@/components/hooks/useResponsive';
import LoadProgress from '@/components/LoadProgress';
import { OverviewGridToolbar } from '@/components/mui-x/datagrid/OverviewGridToolbar';
import DifferenceIcon from '@mui/icons-material/Difference';
import FemaleIcon from '@mui/icons-material/Female';
import FolderCopyIcon from '@mui/icons-material/FolderCopy';
import MaleIcon from '@mui/icons-material/Male';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import { Box, Button, FormControl, Grid, InputLabel, MenuItem, Select, SelectChangeEvent, Tooltip } from '@mui/material';
import {
    GridColDef,
    GridColumnVisibilityModel
} from '@mui/x-data-grid';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { MoreTime, Transgender } from '@nine-thirty-five/material-symbols-react/sharp';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { enqueueSnackbar } from 'notistack';
import React, { useState } from 'react';

export const Route = createFileRoute('/_auth/scoutcards')({
    component: ScoutcardsComponent,
    errorComponent: ({ error }) => {
        if (error) {
            return <ApiErrorComponent error={error as Error} />
        } else {
            return <ApiErrorComponent />
        }
    },
})

function ScoutcardsComponent() {

    const scoutcardExportQuery = useSuspenseQuery(
        scoutcardExportQueryOptions(),
    )
    const scoutcardExports = scoutcardExportQuery.data

    const onSuccess = () => {
        enqueueSnackbar(t('message.scoutcard.export.success'), { variant: 'success' });
    }
    const onError = () => {
        enqueueSnackbar(t('message.scoutcard.export.error'), { variant: 'error' });
    }

    const createFullExportMutation = useCreateFullExportMutation(onSuccess, onError)
    const createDeltaExportMutation = useCreateDeltaExportMutation(onSuccess, onError)
    const createDailyExportMutation = useCreateDailyExportMutation(onSuccess, onError)

    const [selectedExportUuid, setSelectedExportUuid] = useState<string>((scoutcardExports && scoutcardExports[0].uuid) || '');

    const handleExportChange = (event: SelectChangeEvent) => {
        setSelectedExportUuid(event.target.value as string);
    };

    const scoutcardViewQuery = useSuspenseQuery(
        scoutcardRecordsQueryOptions(selectedExportUuid)
    )
    const scoutcardView = scoutcardViewQuery.data

    const { xsmall, small, medium, large, xlarge } = useResponsive()

    const columns: GridColDef[] = []

    columns.push(
        {
            field: 'scoutId',
            headerName: 'Scout-ID',
        },
        {
            field: 'titlePrefix',
            headerName: 'Titel',
        },
        {
            field: 'firstname',
            headerName: 'Vorname',
        },
        {
            field: 'name',
            headerName: 'Nachname',
        },
        {
            field: 'titlePostfix',
            headerName: 'Titel',
        },
        {
            field: 'sex',
            headerName: 'Geschlecht',
            renderCell: (params) => {
                const sex = params.row.sex;

                if ('MALE' == sex) {
                    return (
                        <Tooltip title={t('enum.member.sex.' + sex)}>
                            <Box style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", height: '100%' }}>
                                <MaleIcon />
                            </Box>
                        </Tooltip>
                    )
                } else if ('FEMALE' == sex) {
                    return (
                        <Tooltip title={t('enum.member.sex.' + sex)}>
                            <Box style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", height: '100%' }}>
                                <FemaleIcon />
                            </Box>
                        </Tooltip>
                    )
                } else if ('DIVERS' == sex) {
                    return (
                        <Tooltip title={t('enum.member.sex.' + sex)}>
                            <Box style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", height: '100%' }}>
                                <Transgender />
                            </Box>
                        </Tooltip>
                    )
                } else {
                    return (
                        <Box style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", height: '100%' }}>
                            <QuestionMarkIcon />
                        </Box>
                    )
                }
            },
        },
        {
            field: 'street',
            headerName: 'Adresse',
        },
        {
            field: 'postcode',
            headerName: 'PLZ',
        },
        {
            field: 'city',
            headerName: 'Ort',
        },
        {
            field: 'orgName',
            headerName: 'Organisation',
        },
        {
            field: 'section',
            headerName: 'Stufe',
        },
        {
            field: 'jltext',
            headerName: 'Funktion',
        },
        {
            field: 'code',
            headerName: 'Code',
        },
        {
            field: 'state',
            headerName: 'Status',
        }
    )

    const initialVisibilityModel = {
        member: xsmall || small || medium,
        firstname: large || xlarge,
        lastname: large || xlarge,
        scoutId: large || xlarge,
    } as GridColumnVisibilityModel

    const [visibilityModel, setVisibilityModel] = usePersistedVisibilityModel('scoutcard', initialVisibilityModel)
    const handleVisibilityModelChange = React.useCallback((model: GridColumnVisibilityModel) => {
        setVisibilityModel(model)
    }, [setVisibilityModel]);

    const gridPageSize = useGridPageSize()
    const pageSizeOptions = gridPageSize.getPageSizeOptions('scoutcard', 35)
    const [paginationModel, setPaginationModel] = React.useState(gridPageSize.getPaginationModel('scoutcard', 35));

    const refreshData = () => {
        scoutcardViewQuery.refetch()
    }

    const createFullExport = () => {
        createFullExportMutation.mutate()
    }

    const createDeltaExport = () => {
        createDeltaExportMutation.mutate()
    }

    const createDailyExport = () => {
        createDailyExportMutation.mutate()
    }

    if (
        scoutcardViewQuery.isLoading ||
        scoutcardViewQuery.isFetching ||
        !scoutcardView
    ) {
        return <LoadProgress />
    }

    return (
        <Box sx={{
            height: { xs: `calc(100% - 50px)` }
        }}>
            <Grid container>
                <Grid size={{ lg: 3, sm: 6, xs: 12 }} sx={{ padding: 1 }}>
                    <FormControl fullWidth variant='standard' sx={{ minWidth: 120 }}>
                        <InputLabel>{t('label.scoutcard.export')}</InputLabel>
                        <Select
                            value={selectedExportUuid}
                            onChange={handleExportChange}>
                            {scoutcardExports && scoutcardExports.map((exportItem) => {
                                return <MenuItem key={exportItem.uuid} value={exportItem.uuid}>{t('enum.scoutcardExportType.' + exportItem.type)} {dayjs(exportItem.createDate).format('DD.MM.YYYY')}</MenuItem>
                            })}
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>
            <DataGridPremium
                columns={columns}
                rows={scoutcardView}
                getRowId={(row) => {
                    return row.uuid || ''
                }}
                pagination
                pageSizeOptions={pageSizeOptions}
                paginationModel={paginationModel}
                onPaginationModelChange={(model) => {
                    setPaginationModel(model)
                    gridPageSize.handlePaginationModelChange('scoutcard', model)
                }}
                columnVisibilityModel={visibilityModel}
                onColumnVisibilityModelChange={handleVisibilityModelChange}
                slots={{
                    toolbar: (props) => {
                        return <OverviewGridToolbar {...props} scope="scoutcard" refresh={refreshData} >
                            <Tooltip title={t('tooltip.scoutcard.export.full')}>
                                <Button variant="text" startIcon={<FolderCopyIcon />} onClick={createFullExport}>
                                    {t('action.scoutcard.export.full')}
                                </Button>
                            </Tooltip>
                            <Tooltip title={t('tooltip.scoutcard.export.delta')}>
                                <Button variant="text" startIcon={<DifferenceIcon />} onClick={createDeltaExport}>
                                    {t('action.scoutcard.export.delta')}
                                </Button>
                            </Tooltip>
                            <Tooltip title={t('tooltip.scoutcard.export.daily')}>
                                <Button variant="text" startIcon={<MoreTime />} onClick={createDailyExport}>
                                    {t('action.scoutcard.export.daily')}
                                </Button>
                            </Tooltip>
                        </OverviewGridToolbar>
                    }
                }}
                slotProps={{
                    toolbar: {
                        csvOptions: {
                            disableToolbarButton: true
                        },
                        printOptions: {
                            disableToolbarButton: true
                        }
                    }
                }}
                density='compact'
                hideFooter={true}
                disableDensitySelector
                disableAggregation
                disableColumnPinning
                disableColumnReorder
                disableRowGrouping
                disableRowSelectionOnClick
                disablePivoting
                showToolbar />
        </Box >
    );
}
